import { fromCookie } from '@lingui/detect-locale';
import * as Sentry from '@sentry/tanstackstart-react';
import { StartClient } from '@tanstack/react-start/client';
import { startTransition, StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';

import type { DehydratedRouter } from '#modules/user/features/auth/utils/alignDehydratedAuth';
import { groupChunkErrorEvent } from '#features/analytics/sentry/chunkErrorEvent';
import { demoteShownErrorEvent } from '#features/analytics/sentry/demoteShownErrors';
import { resolveSentryDsn, resolveSentryPlatform } from '#features/analytics/sentry/dsn';
import { restoreIdentity } from '#features/analytics/sentry/identity';
import { scrubTelemetry } from '#features/analytics/sentry/scrub';
import { captureFirstTouch } from '#features/analytics/utils/firstTouch';
import { prewarmAppCheckToken } from '#features/api/utils/appCheck';
import { requestChunkReload } from '#features/error/utils/chunkReload';
import { startHydrationWatchdog } from '#features/error/utils/hydrationWatchdog';
import { isContentNotFoundSentryEvent } from '#features/error/utils/isContentNotFoundError';
import { dynamicActivate, getLocaleFromPath, isLocaleValid } from '#features/i18n';
import { i18n } from '#features/i18n/i18n';
import {
  resolveNativeBootLocale,
  setLocaleCookie,
  setNativeLocale,
} from '#features/i18n/utils/nativeLocale';
import { scheduleImageStoreHealth } from '#features/image-store/utils/scheduleHealth';
import { hideNativeSplashOnFirstRender } from '#features/layout/utils/nativeSplash';
import { reportPushOpen } from '#features/notifications/utils/pushOpen';
import { envConfig } from '#constants/envConfig';
import { isChunkLoadError } from '#utils/isChunkLoadError';
import { getCssPreloadDep, optOutOfRouterSelfReload } from '#utils/isCssPreloadError';
import { installEs2023ArrayPolyfills } from '#utils/polyfills';
import { getRouter } from './router';

// Должно выполниться раньше любого кода роутов и компонентов: они зовут toSorted/toReversed,
// которых нет в Safari < 16.4. Именно вызов, а не голый import — при "sideEffects": false
// импорт ради побочного эффекта бандлер выбрасывал, и полифилла в сборке не оказывалось.
installEs2023ArrayPolyfills();

// Инициализация стоит ДО любого await ниже: загрузка языка, чтение токена и сборка роутера в
// приложении могут упасть, а упавший до Sentry.init старт - белый экран без единого события
// (так и было: роутер нужен трассировке, и init ждал его). Роутер Sentry получает позже,
// через addIntegration.
// The native shell reports too: CapacitorHttp patches fetch to native networking, so
// Sentry's ingest requests carry no browser Origin and CORS never applies (the same
// reason the GraphQL client works from the https://localhost app origin).
// resolveSentryDsn() routes web / android / ios × dev / prod to separate GlitchTip projects.
Sentry.init({
  dsn: resolveSentryDsn(),
  environment: envConfig.SENTRY_ENVIRONMENT,
  initialScope: {
    tags: { platform: resolveSentryPlatform(), runtime: 'javascript' },
  },
  // Какая это сборка — известно на этапе сборки, поэтому ставится здесь, а не тегом после
  // инициализации: события успевают уйти раньше, чем резолвится любой асинхронный источник
  // версии, и самые интересные (падение на старте) уезжали бы безымянными.
  //
  // `release` совпадает со строкой, под которой плагин заливает sourcemaps (vite.config.ts,
  // sentryRelease) — разъедутся, и стектрейсы перестанут разворачиваться.
  // `dist` внутри релиза различает сборки приложения: веб-версия у apk 12 и apk 13 одна и та
  // же, а ломается обычно ровно одна из них. В вебе его нет — различать нечего.
  release: import.meta.env.SENTRY_RELEASE_NAME,
  dist: envConfig.IS_NATIVE ? String(import.meta.env.NATIVE_VERSION_CODE) : undefined,
  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/tanstackstart-react/configuration/options/#sendDefaultPii
  sendDefaultPii: true,
  // Трассировка роутера подключается позже (addIntegration ниже), когда роутер создан.
  // Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }) выключен намеренно.
  enableLogs: true,
  // Шум, на который нельзя ответить кодом — он приходит не из приложения:
  // • «Object Not Found Matching Id…» — вброс расширения браузера (Outlook SafeLinks и родня),
  //   голое отклонение промиса без стека и без единого нашего кадра;
  // • «The WKWebView was deallocated…» — внутренний мост Chrome на iOS: страницу выгрузили
  //   раньше, чем он донёс ответ. Прилетает из CriOS, а не из нашего нативного шелла, и стека
  //   у него тоже нет.
  // Обе группы читаются в списке как настоящие ошибки уровня error и отвлекают от новых.
  ignoreErrors: ['Object Not Found Matching Id', 'The WKWebView was deallocated'],
  // Событие, у которого верхний кадр стека принадлежит не нам, а надстройке над страницей.
  // Ответить на него кодом нельзя: падает чужой скрипт в чужом окружении, у одного посетителя
  // с конкретным расширением.
  //
  // Считали за трое суток: из 30 новых issue таких было десять – MetaMask, переводчики, менеджеры
  // паролей, плюс мост Safari под схемой webkit-masked-url. Треть всего потока, и она вытесняет
  // из списка настоящие поломки. Отсекаем на отправке, а не фильтром в интерфейсе: так они не
  // тратят ни квоту, ни внимание.
  denyUrls: [
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
    /^webkit-masked-url:/i,
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // We recommend adjusting this value in production.
  // Learn more at https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: 1.0,
  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error.
  // Learn more at https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
  replaysSessionSampleRate: 0.2,
  replaysOnErrorSampleRate: 1.0,
  beforeSend(event, hint) {
    if (isContentNotFoundSentryEvent(event, hint)) {
      return null;
    }

    // Раньше остальных: падение протухшего чанка приходит и сюда, и в demoteShownErrorEvent,
    // а группировать его надо в одну issue независимо от того, показали мы человеку экран или нет.
    // Последним: чистка не должна мешать разбору выше, а утечь мимо неё не может ничего.
    return scrubTelemetry(demoteShownErrorEvent(groupChunkErrorEvent(event, hint), hint));
  },
  // Адрес страницы с одноразовым токеном (сброс пароля, подтверждение почты) попадает не только
  // в ошибки, но и в крошки навигации, трассировку и логи - чистим каждый канал (см. scrub).
  beforeBreadcrumb: scrubTelemetry,
  beforeSendTransaction: scrubTelemetry,
  beforeSendLog: scrubTelemetry,
  enabled: !envConfig.IN_DEV,
});

const askForChunkReload = () =>
  requestChunkReload({
    storage: sessionStorage,
    reload: () => window.location.reload(),
    report: (outcome) => {
      Sentry.addBreadcrumb({
        category: 'chunk-reload',
        level: outcome === 'scheduled' ? 'info' : 'warning',
        message: `chunk reload: ${outcome}`,
      });
    },
  });

// Гидрация не состоялась — вкладка застряла пустой оболочкой. Считаем упавшие ассеты и, если
// приложение так и не смонтировалось, даём вкладке ровно одну перезагрузку. Разбор — в
// hydrationWatchdog: это единственный канал, который срабатывает, когда чанк оборвался сетью
// (net::ERR_FAILED), а не пропал с сервера (404).
let failedAssets = 0;

window.addEventListener(
  'error',
  (event) => {
    const target = event.target;

    if (target instanceof HTMLScriptElement || target instanceof HTMLLinkElement) {
      const url = target instanceof HTMLScriptElement ? target.src : target.href;

      if (url.includes('/assets/')) {
        failedAssets += 1;
      }
    }
  },
  // Ошибки загрузки ресурсов не всплывают — ловим на погружении.
  true
);

startHydrationWatchdog({
  storage: sessionStorage,
  reload: () => window.location.reload(),
  isHydrated: () => document.documentElement.dataset.hydrated === 'true',
  failedAssets: () => failedAssets,
  isNative: envConfig.IS_NATIVE,
  report: (outcome) => {
    Sentry.addBreadcrumb({
      category: 'hydration-watchdog',
      level: outcome === 'reloaded' ? 'warning' : 'info',
      message: `hydration watchdog: ${outcome} (failed assets: ${failedAssets})`,
    });
  },
});

// Хелпер Vite шлёт это событие из двух мест сразу, и вести себя с ними надо по-разному
// (подробный разбор — в isCssPreloadError):
//
// • отказ preload-ссылки на СТИЛЬ — доброкачественный: сам импорт всё равно отработает, а с
//   "Disable cache" в DevTools префетч гонится с настоящим запросом и ошибка вовсе ложная.
//   Такое гасим, иначе падает вся навигация по роуту;
// • отказ САМОГО динамического импорта. Тут preventDefault() смертелен: хелпер устроен как
//   `baseModule().catch(handlePreloadError)`, а обработчик при погашенном событии не бросает и
//   ничего не возвращает — промис импорта резолвится значением undefined, и TanStack падает на
//   `res[exportName ?? 'default']` с TypeError, которого не узнаёт ни одна из наших защит.
//   Такое НЕ гасим: пусть летит обычным реджектом с внятным текстом.
window.addEventListener('vite:preloadError', ((event: Event) => {
  const { payload } = event as Event & { payload?: unknown };
  const dep = getCssPreloadDep(payload);

  if (dep === undefined) {
    // Реджект пойдёт дальше как есть — но сначала отбираем у роутера его собственную,
    // неуправляемую перезагрузку: восстановление должно быть одно. См. сам хелпер.
    optOutOfRouterSelfReload(payload);

    return;
  }

  event.preventDefault();

  // Погасить мало: если стиль пропал по-настоящему (выкат), хелпер уже пометил его в `seen` и
  // второй попытки не сделает — человек останется на странице без оформления и без единого
  // события. Спрашиваем сервер про сам файл: 404 значит протухшую сборку, всё прочее — гонку.
  void fetch(dep, { cache: 'no-store', credentials: 'same-origin' })
    .then((response) => (response.status === 404 ? askForChunkReload() : undefined))
    .catch(() => undefined);
}) as EventListener);

window.addEventListener('unhandledrejection', (event) => {
  if (!isChunkLoadError(event.reason)) {
    return;
  }

  void askForChunkReload();
});

// Before hydration and before any route code: document.referrer and the landing URL are only
// trustworthy on the very first page, and a signup can happen on it (the OAuth buttons are on the
// landing page itself). Runs once per browser — see captureFirstTouch.
captureFirstTouch();

// Web push opens land here: the service worker either opens a window on the target URL or
// navigates the focused one, and both are full loads, so this entry runs with the marker in the
// address. Read before any route code for the same reason as above — routes normalise search
// params and unknown ones do not survive that. The native app never arrives this way; its taps
// are handled in useNativePushTap.
reportPushOpen(window.location.search);

// The lang should be set by the server

const locale = (() => {
  const cookieLocale = fromCookie('locale');

  if (cookieLocale && isLocaleValid(cookieLocale)) {
    return cookieLocale;
  }

  return getLocaleFromPath(window.location);
})();

// Метки и владелец ставятся сразу после init и до первого await: ранние события ими же и нужны.
if (locale) {
  Sentry.setTag('locale', locale);
}

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
if (timeZone) {
  Sentry.setTag('timezone', timeZone);
}

const acceptLanguages =
  Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language].filter(Boolean);

if (acceptLanguages.length > 0) {
  Sentry.setTag('accept_language', acceptLanguages.join(','));
}

// Кто это — сразу, из хранилища, не дожидаясь ответа `me`: у сетевых ошибок профиль как раз и не
// грузится, и без этого самый массовый класс ошибок уезжал без владельца (GlitchTip 25.08: сбой
// DNS на Android, поле user пустое). Профиль при загрузке перезапишет личность свежей.
restoreIdentity();

// Платформа и сборка метками — чтобы разложить любую ошибку по устройствам, не читая User-Agent.
// Сборка берётся из константы, а не из `App.getInfo()`: тег нужен на самых ранних событиях, а
// значение то же самое — веб-бандл едет внутри apk и разойтись с ним не может.
Sentry.setTag(
  'app_build',
  envConfig.IS_NATIVE ? String(import.meta.env.NATIVE_VERSION_CODE) : 'web'
);

/*
  Приложение: язык выбирается здесь, до роутера, и его каталог включается сразу. Оболочка
  приложения отрисована заранее с английским, и без этого гидрация перебивала выбор человека
  (см. resolveNativeBootLocale). Кука пишется тем же значением, чтобы все последующие чтения
  – маршруты, переключатель, сверка useNativeLocaleSync – видели один и тот же язык.
*/
if (envConfig.IS_NATIVE) {
  const bootLocale = await resolveNativeBootLocale(fromCookie('locale'));
  setLocaleCookie(bootLocale);
  // Native iOS calls/widgets read Preferences too, including on the first launch.
  await setNativeLocale(bootLocale);
  // Оболочка несёт `lang="en"`: читалка и автоперевод WebView иначе считали бы русский текст английским.
  document.documentElement.lang = bootLocale;
  await dynamicActivate(i18n, bootLocale);

  /*
    Адрес старта – сразу окончательный, до создания маршрутизатора: цепочка редиректов
    `/` → `/ru` → `/ru/dashboard` на холодном старте иногда падала внутри маршрутизатора и
    оставляла белый экран (см. resolveNativeBootPath).
  */
  const [{ getNativeToken }, { isLikelyJwtToken }, { resolveNativeBootPath }] = await Promise.all([
    import('#features/api/utils/nativeAuth'),
    import('#modules/user/features/auth/utils/isLikelyJwtToken'),
    import('#features/layout/utils/nativeBootPath'),
  ]);
  const bootPath = resolveNativeBootPath({
    pathname: window.location.pathname,
    locale: bootLocale,
    isAuth: isLikelyJwtToken(await getNativeToken().catch(() => null)),
  });
  if (bootPath !== window.location.pathname) {
    window.history.replaceState(
      window.history.state,
      '',
      `${bootPath}${window.location.search}${window.location.hash}`
    );
  }
}

// TanStack Router (v1.131) records every scroll event under the key of the
// *current* router location. During a route transition the location already
// points at the destination, so containers that auto-scroll via rAF (the
// PopularGames carousel) get recorded under the NEW page's key — the router
// then "restores" that entry instead of resetting the window to top, and the
// destination opens scrolled to the bottom. Suppress scroll events from
// opted-out containers before the router's capture listener (registered
// inside getRouter below) sees them.
//
// The router is now on 1.170, where scroll tracking is keyed to the *from*
// location — so this guard is most likely redundant. It is kept for now
// because the only way to tell is to open the PopularGames carousel and
// navigate away: drop it once that has been checked on dev.
document.addEventListener(
  'scroll',
  (event) => {
    if (
      event.target instanceof Element &&
      event.target.closest('[data-scroll-restoration-ignore]')
    ) {
      event.stopImmediatePropagation();
    }
  },
  true
);

// Start's StartClient builds the router itself from the `src/router.tsx` entry; getRouter()
// is memoised there, so awaiting it here hands Sentry the very same instance instead of a
// second, never-navigated router.
const router = await getRouter();

/*
  Приложение: заготовка оболочки гостевая, и без этого шапка около секунды рисует «Войти» вошедшему
  (см. alignDehydratedAuth). Делаем до гидрации, пока StartClient не наложил её на контекст.
*/
if (envConfig.IS_NATIVE) {
  const { alignDehydratedAuth } = await import(
    '#modules/user/features/auth/utils/alignDehydratedAuth'
  );
  const { isAuth, authStatus } = router.options.context;
  alignDehydratedAuth(
    (window as unknown as { $_TSR?: { router?: DehydratedRouter } }).$_TSR?.router,
    { isAuth, authStatus }
  );
}

// Трассировка переходов: тот же экземпляр, что получит гидрация.
Sentry.addIntegration(Sentry.tanstackRouterBrowserTracingIntegration(router));

/*
  Приложение: экран запуска (знак и полоска из оболочки) держится до первого отрисованного экрана,
  то есть уже после ожидания языка, каталога и адреса старта выше; нативный сплэш к этому моменту
  давно снят скриптом оболочки. Токен App Check начинаем получать сразу за этим, в фоне: к входу
  или регистрации он будет готов, а запуску ничего не стоит (сами запросы его больше не ждут).
  В вебе оба вызова – пустые.
*/
hideNativeSplashOnFirstRender(router, () => {
  prewarmAppCheckToken();
  /*
    Прогрев хранилища картинок приложения: иконки и обложки выбора игры, раз в сутки, в простое
    после старта (сам график – в schedulePrewarm). Модуль грузится отдельно и только в приложении:
    веб-сборке он не нужен вовсе.
  */
  if (envConfig.IS_NATIVE) {
    void import('#features/game/utils/prewarmCatalogs')
      .then(({ scheduleCatalogPrewarm }) =>
        scheduleCatalogPrewarm(router.options.context.queryClient)
      )
      .catch(() => undefined);
    void import('#modules/finder/components/form/SelectGameCommand/prewarmPickerArt')
      .then(({ prewarmPickerArt }) => prewarmPickerArt(router.options.context.queryClient))
      .catch(() => undefined);
  }
  /*
    Здоровье хранилища: жалоба в GlitchTip через полминуты (только если есть на что) и снимок на
    бэкенд через минуту, оба в простое. Сейчас ставятся только два таймера, сами модули грузятся
    внутри них (см. scheduleHealth) – первому кадру это ничего не стоит. В вебе – пусто.
  */
  scheduleImageStoreHealth();
});

// React 19 render-phase error reporter. onCaughtError/onUncaughtError receive
// { componentStack } — the only reliable way to identify which component threw
// in a minified production bundle. Logs to console and Sentry together so we
// get the info regardless of whether Sentry is enabled (dev) or not.
const reportReactError =
  (kind: 'Uncaught' | 'Caught' | 'Recoverable') =>
  (error: unknown, errorInfo: { componentStack?: string }) => {
    const err = error instanceof Error ? error : new Error(String(error));

    // Протухшая сборка: у человека HTML со старыми хешами, чанк на сервере уже удалён. React
    // ловит это границей ошибок, поэтому unhandledrejection не сработает — просим перезагрузку
    // отсюда. Решение общее с границей и с остальными каналами (см. requestChunkReload), так что
    // лишнего таймера не появится, даже если фолбэком окажется не DefaultCatchBoundary.
    if (isChunkLoadError(err)) {
      void askForChunkReload();
    }

    const componentStack = errorInfo?.componentStack ?? '(no componentStack)';
    // eslint-disable-next-line no-console
    console.error(`[React ${kind}]`, err.message, '\nComponent stack:', componentStack, '\n', err);
    Sentry.captureException(err, {
      level: kind === 'Recoverable' ? 'warning' : 'error',
      tags: { react_error_kind: kind },
      extra: { componentStack },
    });
  };

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <StartClient />
    </StrictMode>,
    {
      onUncaughtError: reportReactError('Uncaught'),
      onCaughtError: reportReactError('Caught'),
      onRecoverableError: reportReactError('Recoverable'),
    }
  );
});

// Web push only. The native shell receives notifications through FCM directly
// (features/notifications/utils/nativePush), and a WebView happens to expose
// `serviceWorker` — so without this gate the app registered a second, web-side
// notification path alongside the native one. Same class of bug as the raw permission
// prompt the bell used to fire in native.
if (!envConfig.IS_NATIVE && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/firebase-messaging-sw.js').catch((error) => {
      console.error('Failed to register service worker', error);
    });
  });
}
